import React, { useState } from "react";
import { Button, buttonVariants } from "../../ui/button";
import { cn } from "../../lib/utils";
import { getDateTimeFormate } from "../../sharedfiles/helper";
import { COMMON_MSGS, NoteInfo } from "../../sharedfiles/tooltipMsgs";
import Tooltips from "../Common/Tooltip";
import { deleteNotes, editNotes } from "./addNoteSlice";
import { useDispatch } from "react-redux";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import NoDataFound from "../Common/table/NoDataFound";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { useColumnPreferences } from "../../sharedfiles/hooks/useColumnPreferences";
import TableToolbar from "../Common/table/TableToolbar";

const NotesTable = ({ data, viewOnlyuser, headers }) => {
    const dispatch = useDispatch();

    const [openEdit, setopenEdit] = useState(false);
    const [currentNote, setCurrentNote] = useState(null);
    const [editedNote, setEditedNote] = useState("");
    const [editedCategory, setEditedCategory] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [deletingIds, setDeletingIds] = useState([]);
    const [deleteItem, setDeleteItem] = useState(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

    const cooldownDuration = 2000;

    const handleEdit = (item) => {
        setCurrentNote(item);
        setEditedNote(item.note);
        setEditedCategory(item.category);
        setopenEdit(true);
    };

    const confirmDelete = (item) => {
        setDeleteItem(item);
        setOpenDeleteDialog(true);
    };

    const handleDelete = () => {
        if (deleteItem && !deletingIds.includes(deleteItem.id)) {
            setDeletingIds((prev) => [...prev, deleteItem.id]);
            dispatch(deleteNotes(deleteItem.playerId, deleteItem.id));
            setTimeout(() => {
                setDeletingIds((prev) => prev.filter((id) => id !== deleteItem.id));
            }, cooldownDuration);
        }
        setOpenDeleteDialog(false);
    };

    const handleSave = () => {
        if (!isSaving && currentNote && editedNote.trim() && editedCategory.trim()) {
            setIsSaving(true);

            const payload = {
                category: editedCategory,
                note: editedNote,
            };

            dispatch(editNotes(currentNote.playerId, currentNote.id, payload));
            setTimeout(() => {
                setIsSaving(false);
                setopenEdit(false);
            }, cooldownDuration);
        }
    };

    const handleCloseModal = () => {
        setopenEdit(false);
        setEditedNote('')
    };
    const resetInputtFields = () => {
        setEditedNote("");
        // setEditedCategory("");
    };

    const isSaveDisabled =
        !editedNote.trim() ||
        !editedCategory.trim()
    // (editedNote === currentNote?.note && editedCategory === currentNote?.category)

    // The table below combines the `headers` prop with a hardcoded trailing
    // "Actions" column - collected here (same sources as before) so
    // reorder/hide can hook into it.
    const noteHeaders = [...headers.map((h) => h.label), "Actions"];
    const noteTooltips = [...headers.map((h, ind) => NoteInfo[ind]), COMMON_MSGS.Actions];
    const noteRowAccessors = [
        (item) => item.platFormUser,
        (item) => item.playerId,
        (item) => getDateTimeFormate(item.createdDate) || "-",
        (item) => item.category,
        (item) => item.note,
        (item) => item.platFormUserId,
        (item) => (item.updatedById === 0 ? "-" : item.updatedById),
        (item) => getDateTimeFormate(item.updatedDate) || '-',
        (item) => (
            <div className="flex gap-2">
                <span
                    onClick={() => !viewOnlyuser && handleEdit(item)}
                    className={cn(
                        buttonVariants({ variant: "default", size: "sm" }),
                        viewOnlyuser ? "opacity-50 pointer-events-none" : "cursor-pointer"
                    )}
                >
                    Edit
                </span>
                <Button
                    variant="destructive"
                    size="sm"
                    type="button"
                    onClick={() => confirmDelete(item)}
                    disabled={viewOnlyuser || deletingIds.includes(item.id)}
                >
                    {deletingIds.includes(item.id) ? "Deleting..." : "Delete"}
                </Button>
            </div>
        ),
    ];
    const { visibleIndices, allColumns, toggleVisibility, reorder, resetToDefault } =
        useColumnPreferences("addNoteAddNotesTable", noteHeaders);

    return (
        <>
            <AlertDialog open={openDeleteDialog} onOpenChange={setOpenDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete this note? This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <Dialog open={openEdit} onOpenChange={handleCloseModal}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="text-lg font-semibold">{currentNote ? 'Edit Note' : 'Add New Note'}</DialogTitle>
                    </DialogHeader>
                    <div className='flex-1 overflow-y-auto space-y-4'>
                        <div className="rounded-lg border bg-card p-6 space-y-4">
                            <div>
                                <label className='text-sm font-medium text-foreground block mb-2'>Note</label>
                                <textarea
                                    value={editedNote}
                                    onChange={(e) => setEditedNote(e.target.value)}
                                    placeholder="Enter your note here..."
                                    rows="5"
                                    className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                                />
                            </div>
                            {isSaveDisabled && (
                                <div className="text-sm text-red-500 flex items-center gap-1">⚠ Please enter valid comments</div>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center justify-end gap-3 pt-4 border-t">
                        <Button
                            onClick={handleCloseModal}
                            variant="outline"
                            type="button"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            variant="default"
                            type="button"
                            disabled={isSaveDisabled || isSaving}
                        >
                            {isSaving ? "Saving..." : "Save Changes"}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <div className="relative rounded-lg border bg-card overflow-hidden shadow-sm">
                <TableToolbar
                    columns={allColumns}
                    onToggle={toggleVisibility}
                    onReorder={reorder}
                    onReset={resetToDefault}
                />
                <Table scrollKey="addNoteAddNotesTable">
                    <TableHeader>
                        <TableRow>
                            {visibleIndices.map((origIdx) => (
                                <TableHead key={origIdx} className="whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                        <span>{noteHeaders[origIdx]}</span>
                                        <Tooltips info={noteTooltips[origIdx]} />
                                    </div>
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>

                    <TableBody>
                        {data && Array.isArray(data) && data.length > 0 ? (
                            data.map((item, index) => (
                                <TableRow key={`${index}_${item.playerId}`}>
                                    {visibleIndices.map((origIdx) => (
                                        <TableCell key={origIdx} className={origIdx === 4 ? "text-sm max-w-xs truncate" : "text-sm"}>
                                            {noteRowAccessors[origIdx](item)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <NoDataFound colspan={visibleIndices.length} />
                        )}
                    </TableBody>
                </Table>
            </div>
        </>
    );
};

export default NotesTable;


